import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Flag } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { fmtIST } from "@/lib/utils";

const STATUS_META = {
  pending_teacher: { label: "Pending with Teacher", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  pending_hod: { label: "Pending with HOD", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-700 border-red-200" },
};

const decisionReason = (a) =>
  a.admin_decision?.reason || a.hod_decision?.reason || a.teacher_decision?.reason || "—";

export default function StudentAppeals() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: eligible = [] } = useQuery({
    queryKey: ["appeal-eligible"],
    queryFn: async () => (await api.get("/appeals/student/eligible")).data,
  });
  const { data: appeals = [] } = useQuery({
    queryKey: ["my-appeals"],
    queryFn: async () => (await api.get("/appeals/student")).data,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) {
      toast.error("Please select a class to appeal");
      return;
    }
    if (!reason.trim()) {
      toast.error("Please provide a reason for your appeal");
      return;
    }
    setSaving(true);
    try {
      await api.post("/appeals/student", { attendance_id: selected, reason });
      toast.success("Appeal submitted to your teacher");
      setSelected("");
      setReason("");
      queryClient.invalidateQueries({ queryKey: ["appeal-eligible"] });
      queryClient.invalidateQueries({ queryKey: ["my-appeals"] });
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title="Attendance Appeals">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-md p-6 h-fit" data-testid="appeal-form-card">
          <div className="flex items-center gap-2 mb-1">
            <Flag className="h-4 w-4 text-slate-500" strokeWidth={1.75} />
            <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">Appeal an absence</h2>
          </div>
          <p className="text-xs text-slate-500 mb-6">
            You can appeal classes from the last 7 days where you were marked absent. Your teacher reviews first, then the HOD gives final approval.
          </p>
          {eligible.length === 0 ? (
            <div className="border border-dashed border-slate-300 rounded-md p-6 text-center" data-testid="appeal-eligible-empty">
              <p className="text-sm text-slate-400">No absent classes in the last 7 days available to appeal.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Class (date · period · subject · teacher)</Label>
                <Select value={selected} onValueChange={setSelected}>
                  <SelectTrigger data-testid="appeal-class-select">
                    <SelectValue placeholder="Select an absent class" />
                  </SelectTrigger>
                  <SelectContent>
                    {eligible.map((s) => (
                      <SelectItem key={s.attendance_id} value={s.attendance_id}
                        data-testid={`appeal-class-option-${s.attendance_id}`}>
                        {s.date} · {s.period_label} · {s.subject_name} · {s.teacher_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="appeal-reason">Reason</Label>
                <Textarea id="appeal-reason" rows={4} data-testid="appeal-reason-input"
                  placeholder="Why should this absence be corrected? (e.g. I was present but marked absent)"
                  value={reason} onChange={(e) => setReason(e.target.value)} />
              </div>
              <Button type="submit" disabled={saving} data-testid="appeal-submit-btn"
                className="w-full bg-slate-950 hover:bg-slate-800 text-white">
                {saving ? "Submitting…" : "Submit appeal"}
              </Button>
            </form>
          )}
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md" data-testid="my-appeals-card">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">My appeals</h2>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Applied On</TableHead>
                <TableHead>Class Date</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Teacher</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Decision Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {appeals.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-400 py-10" data-testid="my-appeals-empty">
                    No appeals submitted yet.
                  </TableCell>
                </TableRow>
              )}
              {appeals.map((a) => {
                const meta = STATUS_META[a.status] || STATUS_META.pending_teacher;
                return (
                  <TableRow key={a.id} data-testid={`appeal-row-${a.id}`}>
                    <TableCell className="font-data text-sm">{fmtIST(a.created_at)}</TableCell>
                    <TableCell className="font-data">{a.date}</TableCell>
                    <TableCell>{a.period_label}</TableCell>
                    <TableCell>{a.subject_name}</TableCell>
                    <TableCell className="text-slate-500">{a.teacher_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={meta.cls} data-testid={`appeal-status-${a.id}`}>
                        {meta.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500 max-w-48" data-testid={`appeal-decision-reason-${a.id}`}>
                      {decisionReason(a)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
}
